import React, { useState } from 'react';
import Layout from '../../components/global/layout';
import { FiPlus } from 'react-icons/fi';
import FormControl from '../../components/common/FormControl';

const MAX_EXCLUSIONS = 3;

const Html = ({
    exclusions = [],
    groupMembers = [],
    stats = null,
    isLeader = false,
    user,
    addExclusion,
    removeExclusion,
}) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [reason, setReason] = useState('');

    // Filter out already-excluded members and self from dropdown
    const excludedIds = exclusions.map(e => e.excludedUserId?._id || e.excludedUserId);
    const availableMembers = groupMembers
        .filter(m => m.id !== user._id && m.userId !== user._id && !excludedIds.includes(m.id) && !excludedIds.includes(m.userId))
        .map(m => ({
            ...m,
            id: m.id || m.userId || m._id,
            displayLabel: `${m.fullName || `${m.firstName} ${m.lastName}`} (${m.email})`,
        }))
        .sort((a, b) => (a.firstName || a.fullName || '').toLowerCase().localeCompare((b.firstName || b.fullName || '').toLowerCase()));

    const handleAdd = () => {
        if (!selectedMember || !reason) return;
        addExclusion(selectedMember.id || selectedMember._id, reason);
        setShowAddForm(false);
        setSelectedMember(null);
        setReason('');
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setSelectedMember(null);
        setReason('');
    };

    return (
        <Layout>
            <div className="flex flex-wrap justify-between items-center gap-y-4 mb-6">
                <div>
                    <h3 className="text-2xl font-semibold text-[#111827]">Already Connected</h3>
                    <p className="text-sm font-normal text-[#75757A]">People on this list will not be placed in your ConnectMeet group</p>
                </div>
            </div>

            {/* GL/AGL/CMM Stats Section — TOP of page */}
            {isLeader && stats && (
                <div className="shadow-box w-full bg-white rounded-lg mb-6 p-6">
                    <h4 className="text-lg font-semibold text-[#111827] mb-4">Group Overview</h4>
                    <div className="flex gap-6 mb-6">
                        <div className="bg-gray-50 rounded-lg p-4 flex-1">
                            <p className="text-sm text-[#75757A]">Members with exclusions</p>
                            <p className="text-2xl font-semibold text-[#111827]">{stats.membersWithExclusionsCount || 0}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 flex-1">
                            <p className="text-sm text-[#75757A]">Total exclusions across group</p>
                            <p className="text-2xl font-semibold text-[#111827]">{stats.totalExclusions || 0}</p>
                        </div>
                    </div>

                    {stats.excludedCounts && stats.excludedCounts.length > 0 && (
                        <>
                            <h5 className="text-sm font-semibold text-[#111827] mb-2">Times excluded by others</h5>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3">Member</th>
                                            <th className="px-6 py-3">Times Excluded</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.excludedCounts.map((item) => (
                                            <tr key={item._id} className="bg-white border-b">
                                                <td className="px-6 py-4 capitalize">{item.fullName || `${item.firstName} ${item.lastName}`}</td>
                                                <td className="px-6 py-4">{item.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* User's Exclusion List */}
            <div className="shadow-box w-full bg-white rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h4 className="text-lg font-semibold text-[#111827]">Your List ({exclusions.length}/{MAX_EXCLUSIONS})</h4>
                        <p className="text-sm text-[#75757A]">This list is private and only visible to you</p>
                    </div>
                    {exclusions.length < MAX_EXCLUSIONS && !showAddForm && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="bg-primary leading-10 h-10 flex items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-lg gap-2"
                        >
                            <FiPlus /> Add Person
                        </button>
                    )}
                </div>

                {/* Add Form */}
                {showAddForm && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <FormControl
                                    type="select"
                                    displayValue="displayLabel"
                                    valueType="object"
                                    placeholder="Search by name or email"
                                    label="Select Member"
                                    theme="search"
                                    value={selectedMember?.id || ''}
                                    options={availableMembers}
                                    onChange={(e) => setSelectedMember(e)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                                <div className="flex flex-col gap-2 mt-1">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="reason"
                                            value="I know this person well"
                                            checked={reason === 'I know this person well'}
                                            onChange={(e) => setReason(e.target.value)}
                                            className="accent-orange-600"
                                        />
                                        <span className="text-sm">I know this person well</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="reason"
                                            value="Other"
                                            checked={reason === 'Other'}
                                            onChange={(e) => setReason(e.target.value)}
                                            className="accent-orange-600"
                                        />
                                        <span className="text-sm">Other</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleAdd}
                                disabled={!selectedMember || !reason}
                                className="bg-orange-600 text-white text-sm px-5 py-2.5 rounded-lg hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add
                            </button>
                            <button
                                onClick={handleCancel}
                                className="bg-gray-200 text-gray-700 text-sm px-5 py-2.5 rounded-lg hover:opacity-80"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Exclusion List */}
                {exclusions.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3">Name</th>
                                    <th className="px-6 py-3">Reason</th>
                                    <th className="px-6 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {exclusions.map((excl) => {
                                    const excluded = excl.excludedUserId || {};
                                    return (
                                        <tr key={excl._id} className="bg-white border-b">
                                            <td className="px-6 py-4 capitalize">
                                                {excluded.fullName || `${excluded.firstName || ''} ${excluded.lastName || ''}`.trim() || 'Unknown'}
                                            </td>
                                            <td className="px-6 py-4">{excl.reason}</td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => removeExclusion(excl._id)}
                                                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 py-4 text-center">Your Already Connected list is empty</p>
                )}
            </div>
        </Layout>
    );
};

export default Html;
